import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/card'
import type { ShopProductDocument } from '../types'

interface ProductCardProps {
  product: ShopProductDocument
}

function getLowestPrice(product: ShopProductDocument): number {
  const enabled = product.variants.filter((v) => v.is_enabled)
  if (enabled.length === 0) return 0
  return Math.min(...enabled.map((v) => v.price))
}

function getOrderedImages(product: ShopProductDocument): string[] {
  const all = product.images.map((i) => i.src).filter(Boolean)
  if (all.length === 0) return []
  const defaultIdx = product.images.findIndex((i) => i.is_default)
  if (defaultIdx > 0) {
    const reordered = [...all]
    const [def] = reordered.splice(defaultIdx, 1)
    if (def) reordered.unshift(def)
    return reordered
  }
  return all
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

const CYCLE_MS = 1200

export function ProductCard({ product }: ProductCardProps) {
  const images = getOrderedImages(product)
  const price = getLowestPrice(product)
  const [index, setIndex] = useState(0)
  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    if (!hovered || images.length < 2) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % images.length)
    }, CYCLE_MS)
    return () => window.clearInterval(id)
  }, [hovered, images.length])

  useEffect(() => {
    if (!hovered) setIndex(0)
  }, [hovered])

  const currentSrc = images[index]

  return (
    <Link
      to={`/shop/${product.id}`}
      className="group block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <Card className="overflow-hidden transition-shadow hover:shadow-md">
        <div className="relative aspect-square overflow-hidden bg-muted">
          {currentSrc ? (
            <AnimatePresence mode="sync" initial={false}>
              <motion.img
                key={currentSrc}
                src={currentSrc}
                alt={product.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </AnimatePresence>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground text-sm">
              No image
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <h3 className="font-medium text-sm leading-tight line-clamp-2">{product.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {price > 0 ? `From ${formatPrice(price)}` : 'Price unavailable'}
          </p>
        </CardContent>
      </Card>
    </Link>
  )
}
